/**
 * StepperElement — F12.H T12.H.4.
 *
 * Stepper — wskaźnik postępu (kropki + numery + tytuły kroków) w układzie
 * horyzontalnym. User może klikać krok żeby przeskoczyć. Różni się od
 * WizardElement brakiem walidacji per krok i wymuszonej sekwencji.
 *
 * Settings: { steps: [{ id, label, icon? }], orientation?: 'horizontal'|'vertical' }
 */

import { useState } from 'react';
import { Check } from 'lucide-react';

import type { LayoutElementDefinition } from '@/components/fields/LayoutElementRegistry';

interface StepConfig {
  id: string;
  label: string;
  icon?: string;
}

export const StepperElement: LayoutElementDefinition = {
  type: 'stepper',
  category: 'interaktywne',
  isContainer: true,
  label: 'Stepper',
  icon: 'CircleDot',
  defaultSettings: () => ({
    steps: [
      { id: 'step-1', label: 'Krok 1' },
      { id: 'step-2', label: 'Krok 2' },
      { id: 'step-3', label: 'Krok 3' },
    ] as StepConfig[],
    orientation: 'horizontal',
  }),
  render: ({ element, children, mode }) => {
    const settings = (element.settings ?? {}) as {
      steps?: StepConfig[];
      orientation?: 'horizontal' | 'vertical';
    };
    const isBuilder = mode === 'builder';
    return (
      <StepperView
        steps={settings.steps ?? []}
        orientation={settings.orientation ?? 'horizontal'}
        children={children}
        isBuilder={isBuilder}
        elementId={element.id}
      />
    );
  },
};

function StepperView({
  steps,
  orientation,
  children,
  isBuilder,
  elementId,
}: {
  steps: StepConfig[];
  orientation: 'horizontal' | 'vertical';
  children: React.ReactNode[];
  isBuilder: boolean;
  elementId: string;
}) {
  const [activeIdx, setActiveIdx] = useState(0);
  const isHoriz = orientation === 'horizontal';

  return (
    <div
      className={`${isBuilder ? 'ring-2 ring-transparent hover:ring-accent-200 transition rounded p-1' : ''}`}
      data-testid={`stepper-element-${elementId}`}
      data-element-type="stepper"
      data-orientation={orientation}
    >
      <div className={isHoriz ? 'flex items-center gap-2' : 'flex flex-col gap-2'}>
        {steps.length === 0 && isBuilder && (
          <p className="text-xs italic text-text-muted">
            (brak kroków — dodaj w panelu właściwości)
          </p>
        )}
        {steps.map((step, idx) => {
          const isActive = idx === activeIdx;
          const isDone = idx < activeIdx;
          return (
            <div
              key={step.id}
              className={isHoriz ? 'flex flex-1 items-center gap-2' : 'flex items-center gap-2'}
            >
              <button
                type="button"
                onClick={() => setActiveIdx(idx)}
                aria-current={isActive ? 'step' : undefined}
                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold transition ${
                  isActive
                    ? 'bg-accent-500 text-white'
                    : isDone
                      ? 'bg-success-500 text-white'
                      : 'bg-bg-tertiary text-text-secondary hover:bg-bg-tertiary/70'
                }`}
              >
                {isDone ? <Check className="h-3 w-3" aria-hidden="true" /> : idx + 1}
              </button>
              <span
                className={`text-sm ${isActive ? 'font-medium text-text-primary' : 'text-text-secondary'}`}
              >
                {step.label}
              </span>
              {isHoriz && idx < steps.length - 1 && (
                <div className="h-px flex-1 bg-border-primary" aria-hidden="true" />
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-4">
        {children.length > 0 ? (
          <div className="space-y-3">{children}</div>
        ) : isBuilder ? (
          <p className="text-xs italic text-text-muted">
            (zawartość kroku pusta — przeciągnij elementy)
          </p>
        ) : null}
      </div>
    </div>
  );
}
